/**
 * A request the map cancelled itself — `map.remove()` on unmount or
 * `setStyle()` replacing a style still loading. Not a failure: map "error"
 * handlers ignore it instead of logging it or showing an error banner.
 */
export function isAbortError(event: unknown): boolean {
  const err = (event as { error?: unknown } | null)?.error ?? event;
  const name = (err as { name?: string } | null)?.name;
  const message = String((err as { message?: string } | null)?.message ?? "");
  return name === "AbortError" || /\babort(ed)?\b/i.test(message);
}

type MapLike = { remove(): void; style?: unknown } | null | undefined;

/**
 * vietmap-gl 6 reports the cancelled load of a style that is still loading
 * on the outgoing Style object, AFTER detaching it from the map — with no
 * listener left it prints "AbortError: signal is aborted" via console.error.
 * Call this right before `setStyle()` / `remove()` so that style has one.
 */
export function releaseStyle(map: MapLike): void {
  const style = (map as { style?: { on?: (type: string, fn: (e: unknown) => void) => void } } | null)?.style;
  style?.on?.("error", (e) => {
    // eslint-disable-next-line no-console
    if (!isAbortError(e)) console.warn("[map] style error:", (e as { error?: unknown })?.error ?? e);
  });
}

/** `map.remove()` without the abort noise of a style still loading. */
export function removeMap(map: MapLike): void {
  if (!map) return;
  releaseStyle(map);
  map.remove();
}

/** A tile request that failed in a way a bad API key would cause. */
export type KeyFailure = {
  key: string;
  /** The failed tile, to probe with `isKeyRefused`. */
  url: string;
  /** 401 / 403 when the error carries it; null for a bare network error. */
  status: number | null;
};

/**
 * A map "error" event for a tile whose `apikey` may have been refused.
 * Vietmap answers a refused key with a 401 that has no CORS headers, so the
 * browser usually reports only "Failed to fetch" — the key then comes from
 * the source's tile URL, and `isKeyRefused` tells it from being offline.
 */
export function keyFailure(event: unknown): KeyFailure | null {
  const e = event as {
    error?: { status?: unknown; url?: unknown; message?: unknown };
    source?: { tiles?: unknown };
    tile?: { tileID?: { canonical?: { z: number; x: number; y: number } } };
  } | null;
  const err = e?.error;
  // Errors from the worker are plain Errors — only the message survives:
  // "AJAXError: Unauthorized (401): https://…?apikey=…".
  const m = /\((\d{3})\):\s*(\S+)/.exec(String(err?.message ?? ""));
  const status = Number(err?.status ?? m?.[1]) || null;
  let url = typeof err?.url === "string" ? err.url : m?.[2];
  if (status && status !== 401 && status !== 403) return null;
  if (!url) {
    const template = Array.isArray(e?.source?.tiles) ? e.source.tiles[0] : null;
    const c = e?.tile?.tileID?.canonical;
    if (typeof template !== "string" || !c) return null;
    url = template.replace("{z}", String(c.z)).replace("{x}", String(c.x)).replace("{y}", String(c.y));
  }
  try {
    const key = new URL(url, location.href).searchParams.get("apikey");
    return key ? { key, url, status } : null;
  } catch {
    return null;
  }
}

/**
 * Does the tile server refuse this request's key? A plain request that fails
 * while a `no-cors` one gets through means the answer had no CORS headers —
 * Vietmap's refusal (good answers always carry them). Both failing = offline.
 */
export async function isKeyRefused(url: string): Promise<boolean> {
  try {
    const res = await fetch(url, { cache: "no-store" });
    return res.status === 401 || res.status === 403;
  } catch {
    try {
      await fetch(url, { mode: "no-cors", cache: "no-store" });
      return true;
    } catch {
      return false;
    }
  }
}
